import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { SiteHeader } from '../../components/SiteHeader';
import { FooterSection } from '../../components/FooterSection';
import './TermsAndConditionsPage.css';

export function TermsAndConditionsPage() {
  useDocumentTitle('Terms and Conditions');

  return (
    <div className="landing terms-page">
      <SiteHeader />

      <main className="terms-page__main">
        <div className="container terms-page__content">
          <h1>Terms and Conditions</h1>
          <p className="terms-page__updated">Last Updated: 19.05.2026</p>

          <h2>Agreement to Our Legal Terms</h2>
          <p>
            These Terms and Conditions (&quot;Legal Terms&quot;) constitute a
            legally binding agreement between you (whether personally or on
            behalf of an entity) (&quot;you&quot;) and David Osmukhin, operating
            as a sole proprietor under the business name Review Lumen
            (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;), with a registered business address at:
          </p>
          <p>
            Rua General Justiniano Padrel 21, 1170-011 Lisbon, Portugal
            <br />
            Phone: <a href="tel:+351932581722">+351 932 581 722</a>
            <br />
            Email:{' '}
            <a href="mailto:support@reviewlumen.com">support@reviewlumen.com</a>
          </p>
          <p>
            These Legal Terms govern your access to and use of our website,
            services, software, subscriptions, messaging systems, automation
            tools, review generation systems, reputation management tools, and
            related offerings (collectively, the &quot;Services&quot;).
          </p>
          <p>
            By accessing or using our Services, you confirm that you have read,
            understood, and agreed to be bound by these Legal Terms. If you do
            not agree, you must discontinue use immediately.
          </p>

          <h2>1. Our Services</h2>
          <p>
            Review Lumen provides reputation management and business growth
            solutions, including but not limited to:
          </p>
          <ul>
            <li>Automated review request systems</li>
            <li>SMS and email customer outreach</li>
            <li>Review monitoring</li>
            <li>Review response assistance</li>
            <li>Reputation marketing tools</li>
            <li>Google Business Profile optimization support</li>
            <li>CRM and third-party software integrations</li>
            <li>Customer reactivation campaigns</li>
            <li>Local SEO-related services</li>
          </ul>
          <p>Our Services are intended for lawful business purposes only.</p>

          <h2>2. Business Structure Disclosure</h2>
          <p>
            Review Lumen is operated as a Portuguese sole proprietorship by David
            Osmukhin.
          </p>
          <p>
            You acknowledge that you are contracting directly with David Osmukhin
            as an independent business operator registered in Portugal, and not
            with a corporation, LLC, or separate legal entity.
          </p>

          <h2>3. Eligibility</h2>
          <p>You must be at least 18 years old to use our Services.</p>
          <p>By using the Services, you represent that:</p>
          <ul>
            <li>All information provided is accurate</li>
            <li>
              You have authority to act on behalf of your business if applicable
            </li>
            <li>You will comply with all applicable laws and regulations</li>
            <li>You will not misuse the Services</li>
          </ul>

          <h2>4. VAT / Portuguese Tax Disclosure</h2>
          <p>
            Review Lumen is operated by David Osmukhin as a sole proprietor
            registered in Portugal.
          </p>
          <p>
            Unless otherwise stated, applicable taxes, including VAT where legally
            required, may be charged in accordance with Portuguese and European
            Union tax laws.
          </p>
          <p>Clients acknowledge and agree that:</p>
          <ul>
            <li>
              Tax treatment may vary depending on the Client&apos;s jurisdiction,
              tax residency, and business status
            </li>
            <li>
              Reverse charge mechanisms may apply to certain business-to-business
              (B2B) transactions where legally applicable
            </li>
            <li>
              Clients are responsible for any taxes, duties, reporting
              obligations, or local compliance requirements applicable within
              their own jurisdiction
            </li>
            <li>
              Third-party payment processors or merchants of record (including
              but not limited to Lemon Squeezy or Stripe) may independently
              calculate, collect, remit, or display taxes where required by law
            </li>
          </ul>
          <p>
            Nothing in these Terms constitutes tax or legal advice. Clients are
            encouraged to consult their own professional advisors regarding
            applicable obligations.
          </p>

          <h2>4. Payments, Billing &amp; Subscriptions</h2>
          <h3>Payment Processors</h3>
          <p>
            Payments may be processed through third-party payment providers
            including but not limited to:
          </p>
          <ul>
            <li>Stripe</li>
            <li>Lemon Squeezy</li>
            <li>Other approved providers</li>
          </ul>
          <p>You agree to comply with their payment terms where applicable.</p>

          <h3>Subscription Billing</h3>
          <ul>
            <li>Subscriptions renew automatically unless canceled</li>
            <li>You authorize recurring charges</li>
            <li>Pricing may change with notice</li>
            <li>Failed payments may result in suspension</li>
          </ul>

          <h3>Refund Policy</h3>
          <p>Unless otherwise required by Portuguese or EU consumer law:</p>
          <ul>
            <li>
              Subscription fees are generally non-refundable after service
              delivery begins
            </li>
            <li>Trial periods may be canceled according to stated terms</li>
            <li>Refund requests are reviewed at our discretion</li>
          </ul>

          <h2>5. Client Responsibilities</h2>
          <p>You are solely responsible for:</p>
          <ul>
            <li>Obtaining proper consent before contacting customers</li>
            <li>
              Compliance with GDPR, TCPA, CAN-SPAM, CASL, and other relevant
              communication laws
            </li>
            <li>Accuracy of uploaded customer data</li>
            <li>Proper use of messaging tools</li>
            <li>Compliance with Google and third-party platform guidelines</li>
          </ul>
          <p>You may not:</p>
          <ul>
            <li>Upload unlawful contact lists</li>
            <li>Send spam</li>
            <li>Violate privacy laws</li>
            <li>Use deceptive practices</li>
            <li>Circumvent opt-outs</li>
          </ul>

          <h2>6. Data Privacy &amp; GDPR</h2>
          <p>We process personal data in accordance with:</p>
          <ul>
            <li>EU General Data Protection Regulation (GDPR)</li>
            <li>Portuguese data protection laws</li>
            <li>Applicable international privacy regulations</li>
          </ul>
          <h3>Data Controller / Processor</h3>
          <ul>
            <li>You are typically the data controller for your customer lists</li>
            <li>
              Review Lumen acts as a service provider / processor where applicable
            </li>
          </ul>
          <h3>Data Storage</h3>
          <p>We may store operational data as necessary for:</p>
          <ul>
            <li>Service delivery</li>
            <li>Legal compliance</li>
            <li>Security</li>
            <li>Account continuity</li>
          </ul>
          <p>Users may request deletion where legally applicable.</p>

          <h2>7. Intellectual Property</h2>
          <p>
            All software, branding, systems, content, automation frameworks,
            website materials, and service methodologies remain the exclusive
            property of Review Lumen unless otherwise stated.
          </p>
          <p>You may not:</p>
          <ul>
            <li>Copy</li>
            <li>Resell</li>
            <li>Reverse engineer</li>
            <li>Replicate</li>
            <li>Redistribute</li>
          </ul>
          <p>without written consent.</p>

          <h2>8. Limitation of Liability</h2>
          <p>To the fullest extent permitted under Portuguese and EU law:</p>
          <ul>
            <li>Services are provided &quot;as is&quot;</li>
            <li>
              We do not guarantee specific business outcomes, rankings, review
              quantities, or revenue increases
            </li>
            <li>
              We are not liable for indirect, incidental, or consequential
              damages
            </li>
            <li>
              Total liability shall not exceed the amount paid by you during the
              previous one (1) month of service or €500, whichever is lower
            </li>
          </ul>
          <p>
            This limitation does not exclude liability where prohibited by law.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless David Osmukhin /
            Review Lumen from any claims, losses, fines, liabilities, or legal
            expenses arising from:
          </p>
          <ul>
            <li>Privacy law violations</li>
            <li>Improper customer consent</li>
            <li>Client misuse of Services</li>
            <li>Regulatory violations</li>
            <li>Third-party disputes related to your campaigns</li>
          </ul>

          <h2>10. Third-Party Services</h2>
          <p>Our Services may integrate with third parties including:</p>
          <ul>
            <li>Google</li>
            <li>Stripe</li>
            <li>Lemon Squeezy</li>
            <li>Twilio</li>
            <li>Embedmyreviews</li>
            <li>Zapier</li>
            <li>Textgrid</li>
            <li>CRM systems</li>
            <li>Social platforms</li>
          </ul>
          <p>We are not responsible for:</p>
          <ul>
            <li>Third-party outages</li>
            <li>API limitations</li>
            <li>External platform policy changes</li>
            <li>Third-party fees</li>
          </ul>

          <h2>11. Termination</h2>
          <p>We reserve the right to suspend or terminate Services if:</p>
          <ul>
            <li>Payments fail</li>
            <li>Laws are violated</li>
            <li>Spam or abuse occurs</li>
            <li>Fraud is suspected</li>
            <li>Platform misuse occurs</li>
          </ul>
          <p>Clients may cancel according to subscription terms.</p>

          <h2>12. Governing Law</h2>
          <p>
            These Legal Terms are governed by the laws of Portugal and applicable
            European Union regulations.
          </p>
          <p>
            Any disputes arising from these Terms shall be subject to the
            exclusive jurisdiction of the competent courts of Portugal, unless
            otherwise required by mandatory consumer protection laws.
          </p>

          <h2>13. Dispute Resolution</h2>
          <p>
            Before formal legal action, parties agree to attempt good-faith
            resolution.
          </p>
          <p>Where legally permitted:</p>
          <ul>
            <li>Portuguese courts shall have jurisdiction</li>
            <li>Mandatory consumer rights remain preserved</li>
            <li>Arbitration may be used only where mutually agreed</li>
          </ul>

          <h2>14. Electronic Communications</h2>
          <p>By using our Services, you consent to receive:</p>
          <ul>
            <li>Emails</li>
            <li>Billing notices</li>
            <li>Legal notices</li>
            <li>System notifications</li>
            <li>Service communications</li>
          </ul>
          <p>
            Electronically delivered notices satisfy legal communication
            requirements.
          </p>

          <h2>15. Marketing Disclaimers</h2>
          <p>Review Lumen does not guarantee:</p>
          <ul>
            <li>Google rankings</li>
            <li>Search engine placement</li>
            <li>AI search inclusion</li>
            <li>Review conversion rates</li>
            <li>Revenue outcomes</li>
            <li>Reputation improvements</li>
          </ul>
          <p>Results depend on numerous external factors.</p>

          <h2>16. Modifications to Terms</h2>
          <p>We reserve the right to update these Legal Terms at any time.</p>
          <p>
            Changes become effective upon publication with updated revision
            dates.
          </p>
          <p>Continued use constitutes acceptance.</p>

          <h2>17. Contact Information</h2>
          <div className="terms-page__contact">
            <p>David Osmukhin / Review Lumen</p>
            <p>Rua General Justiniano Padrel 21</p>
            <p>1170-011 Lisbon</p>
            <p>Portugal</p>
            <p>
              Phone: <a href="tel:+351932581722">+351 932 581 722</a>
            </p>
            <p>
              Email:{' '}
              <a href="mailto:support@reviewlumen.com">support@reviewlumen.com</a>
            </p>
          </div>

          <h2>18. Final Provisions</h2>
          <p>
            If any provision is deemed unenforceable, remaining provisions remain
            valid.
          </p>
          <p>
            These Terms constitute the full agreement between parties and
            supersede prior understandings.
          </p>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
